import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import {
  AlertCircle,
  Download,
  Eye,
  FileCode2,
  Loader,
  ScrollText,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { eventTeamApiService, EventTeamApiError } from '../../services/eventTeamApi';
import type { EventQuestChapterGroup, EventQuestFilePublic, EventQuestManifest } from '../../types/eventQuests';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { ATLAS_DEFAULT_CLUSTER } from '../../utils/atlasItemIcon';
import { buildChapterModel, displayQuestTitle } from '../../utils/questSnbt';
import { validateSnbtUpload, validateSnbtUploads } from '../../utils/questSnbt/files';
import { cn } from '../../utils/cn';
import { ConfirmationModal } from '../ConfirmationModal';
import { CreatorProfile } from '../CreatorProfile';
import { QuestViewerLoadingOverlay, QuestViewerWindow } from './questViewer/QuestViewerWindow';

export interface EventQuestsSectionProps {
  eventId: string;
  perms: EventTeamPerms;
  clusterIds?: string[];
  onChanged?: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

async function downloadBlob(response: Response, filename: string): Promise<void> {
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Event-detail Quests panel: upload / download / re-upload SNBT files
 * and open the in-window chapter viewer. Editing is not offered yet.
 */
export const EventQuestsSection = ({
  eventId,
  perms,
  clusterIds: clusterIdsProp = [],
  onChanged,
}: EventQuestsSectionProps) => {
  const [manifest, setManifest] = useState<EventQuestManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EventQuestFilePublic | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewer, setViewer] = useState<{
    filename: string;
    title: string;
    model: ReturnType<typeof buildChapterModel>;
  } | null>(null);
  const [groupsPreview, setGroupsPreview] = useState<{
    filename: string;
    groups: EventQuestChapterGroup[];
  } | null>(null);
  const [viewerLoading, setViewerLoading] = useState<string | null>(null);
  const [fetchedClusters, setFetchedClusters] = useState<string[]>([]);

  const clusterIds = clusterIdsProp.length > 0 ? clusterIdsProp : fetchedClusters;

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.getEventQuestManifest(eventId);
      setManifest(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quest files');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (clusterIdsProp.length > 0) return;
    eventTeamApiService
      .listServerLinks('event', eventId)
      .then((links) => {
        setFetchedClusters(
          links.map((link) => link.cluster_id).filter((c): c is string => !!c),
        );
      })
      .catch(() => setFetchedClusters([]));
  }, [eventId, clusterIdsProp.length]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const filesByName = useMemo(() => {
    const map = new Map<string, EventQuestFilePublic>();
    for (const f of manifest?.files ?? []) map.set(f.filename, f);
    return map;
  }, [manifest]);

  const chapterGroupsFile = filesByName.get('chapter_groups.snbt') ?? null;
  const missingGroups = manifest?.missing_chapter_groups ?? !chapterGroupsFile;

  const uploadFiles = async (files: File[]) => {
    const { accepted, error: validationError } = validateSnbtUploads(files);
    if (validationError || accepted.length === 0) {
      alert(validationError || 'Only .snbt files with a safe filename are accepted.');
      return;
    }
    setBusy('upload');
    try {
      const formData = new FormData();
      if (accepted.length === 1) formData.append('file', accepted[0] as File, accepted[0].name);
      else accepted.forEach((f, i) => formData.append(`files[${i}]`, f as File, f.name));
      await eventTeamApiService.uploadEventQuests(eventId, formData);
      await load();
      onChanged?.();
    } catch (err) {
      const extra =
        err instanceof EventTeamApiError && err.status === 409 ? ' (chapter group conflict — upload chapter_groups.snbt first, or check group ids)' : '';
      alert((err instanceof Error ? err.message : 'Upload failed') + extra);
    } finally {
      setBusy(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => void uploadFiles(accepted),
    disabled: !perms.canCreate || busy === 'upload',
    multiple: true,
  });

  const handleDownload = async (file: EventQuestFilePublic) => {
    setBusy(`dl:${file.filename}`);
    try {
      const res = await eventTeamApiService.downloadQuestFile(eventId, file.filename);
      await downloadBlob(res, file.filename);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  const handleReplace = async (file: EventQuestFilePublic, incoming: File) => {
    const validationError = validateSnbtUpload(incoming);
    if (validationError) {
      alert(validationError);
      return;
    }
    setBusy(`put:${file.filename}`);
    try {
      const formData = new FormData();
      formData.append('file', incoming, incoming.name);
      await eventTeamApiService.uploadEventQuests(eventId, formData);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Re-upload failed');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await eventTeamApiService.deleteQuestFile(eventId, deleteTarget.filename);
      setDeleteTarget(null);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const openViewer = async (file: EventQuestFilePublic) => {
    setViewerLoading(file.filename);
    try {
      const doc = await eventTeamApiService.getQuestEditor(eventId, file.filename);
      if (file.file_kind === 'chapter_groups') {
        setGroupsPreview({
          filename: file.filename,
          groups: doc.editor.chapter_groups ?? [],
        });
        return;
      }
      const model = buildChapterModel(doc.parsed);
      setViewer({
        filename: file.filename,
        title: doc.editor.title || file.title || model.title,
        model,
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to open viewer');
    } finally {
      setViewerLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-accent-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-amber-300" />
          Quest SNBT ({manifest?.files.length ?? 0})
        </h3>
        {manifest && (
          <span className="text-[11px] text-glass-500">
            {manifest.chapter_count} chapter{manifest.chapter_count === 1 ? '' : 's'}
            {missingGroups ? ' · chapter_groups.snbt required' : ''}
          </span>
        )}
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {perms.canCreate && (
        <div
          {...getRootProps()}
          className={cn(
            'glass-card p-4 border border-dashed transition-colors cursor-pointer',
            isDragActive ? 'border-accent-400 bg-accent-500/10' : 'border-glass-200/25 hover:border-glass-200/50',
            busy === 'upload' && 'opacity-60 pointer-events-none',
          )}
        >
          <input {...getInputProps()} />
          <div className="flex items-center gap-3">
            {busy === 'upload' ? (
              <Loader className="w-5 h-5 animate-spin text-accent-400" />
            ) : (
              <Upload className="w-5 h-5 text-accent-400" />
            )}
            <div>
              <p className="text-sm text-glass-200">
                {missingGroups
                  ? 'Drop chapter_groups.snbt first, then chapter files'
                  : 'Drop .snbt files to upload or replace'}
              </p>
              <p className="text-xs text-glass-500">
                Re-uploading the same canonical name updates the stored file. Max 2 MiB each.
              </p>
            </div>
          </div>
        </div>
      )}

      {chapterGroupsFile && (
        <FileRow
          file={chapterGroupsFile}
          label="Chapter groups"
          perms={perms}
          busy={busy}
          onDownload={() => void handleDownload(chapterGroupsFile)}
          onReplace={(f) => void handleReplace(chapterGroupsFile, f)}
          onDelete={perms.canAdmin ? () => setDeleteTarget(chapterGroupsFile) : undefined}
          onView={() => void openViewer(chapterGroupsFile)}
        />
      )}

      {(manifest?.chapter_groups ?? []).map((group) => {
        const uploaded = group.has_file && group.file ? group.file : null;
        return (
          <div key={group.id} className="glass-card p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm text-white font-medium truncate">{displayQuestTitle(group.title, group.id)}</p>
                <p className="text-[10px] font-mono text-glass-500 truncate">{group.id}</p>
              </div>
              {!uploaded && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-glass-100/10 text-glass-500 border border-glass-200/15 flex-shrink-0">
                  no event chapter
                </span>
              )}
            </div>
            {uploaded ? (
              <FileRow
                file={uploaded}
                embedded
                perms={perms}
                busy={busy}
                onDownload={() => void handleDownload(uploaded)}
                onReplace={(f) => void handleReplace(uploaded, f)}
                onDelete={perms.canAdmin ? () => setDeleteTarget(uploaded) : undefined}
                onView={() => void openViewer(uploaded)}
              />
            ) : (
              <p className="text-xs text-glass-500">
                Unused group — expected unless this group is part of the event.
              </p>
            )}
          </div>
        );
      })}

      {!missingGroups && (manifest?.chapter_groups.length ?? 0) === 0 && (
        <p className="text-sm text-glass-400 text-center py-4">chapter_groups.snbt has no groups.</p>
      )}

      {missingGroups && !perms.canCreate && (
        <p className="text-sm text-glass-400 text-center py-4">No quest files on this event yet.</p>
      )}

      {deleteTarget && (
        <ConfirmationModal
          isOpen
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => void handleDelete()}
          title="Delete quest file"
          message={`Delete ${deleteTarget.filename}? This cannot be undone.`}
          confirmText="Delete"
          isLoading={deleting}
        />
      )}

      {viewerLoading && <QuestViewerLoadingOverlay onClose={() => setViewerLoading(null)} />}

      {viewer && (
        <QuestViewerWindow
          title={viewer.title}
          filename={viewer.filename}
          model={viewer.model}
          clusterId={clusterIds[0] || ATLAS_DEFAULT_CLUSTER}
          clusterOptions={clusterIds}
          onClose={() => setViewer(null)}
        />
      )}

      {groupsPreview &&
        createPortal(
          <div className="fixed inset-0 z-[80]">
            <div className="absolute inset-0 bg-black/55" onClick={() => setGroupsPreview(null)} />
            <div className="absolute inset-8 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[480px] max-h-[80vh] glass-card overflow-hidden flex flex-col">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-glass-200/15">
                <div>
                  <p className="text-sm font-semibold text-white">Chapter groups</p>
                  <p className="text-[11px] text-glass-500">{groupsPreview.filename}</p>
                </div>
                <button type="button" onClick={() => setGroupsPreview(null)} className="p-1.5 text-glass-400 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="overflow-y-auto p-3 space-y-1.5">
                {groupsPreview.groups.length === 0 ? (
                  <p className="text-sm text-glass-400 text-center py-6">No groups in this file.</p>
                ) : (
                  groupsPreview.groups.map((g) => (
                    <div key={g.id} className="rounded-lg bg-glass-100/5 border border-glass-200/10 px-3 py-2">
                      <p className="text-sm text-white">{displayQuestTitle(g.title, g.id)}</p>
                      <p className="text-[10px] font-mono text-glass-500 break-all">{g.id}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

function FileRow({
  file,
  label,
  embedded,
  perms,
  busy,
  onDownload,
  onReplace,
  onDelete,
  onView,
}: {
  file: EventQuestFilePublic;
  label?: string;
  embedded?: boolean;
  perms: EventTeamPerms;
  busy: string | null;
  onDownload: () => void;
  onReplace: (file: File) => void;
  onDelete?: () => void;
  onView: () => void;
}) {
  const inputId = `quest-replace-${file.filename}`;
  return (
    <div className={cn(!embedded && 'glass-card p-3', 'flex items-center justify-between gap-3 flex-wrap')}>
      <div className="min-w-0 flex items-start gap-2">
        <FileCode2 className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          {label && <p className="text-[10px] uppercase tracking-wide text-glass-500">{label}</p>}
          <p className="text-sm text-white truncate">{file.title || file.filename}</p>
          <p className="text-[11px] text-glass-500">
            {file.filename} · {formatBytes(file.size)}
            {file.updated_at ? ' · updated' : ''}
          </p>
          <p className="text-[11px] text-glass-500 inline-flex items-center gap-1">
            Uploaded by <CreatorProfile creatorId={file.uploader_id} inline />
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button"
          onClick={onView}
          className="glass-button-secondary text-xs py-1 px-2 inline-flex items-center gap-1"
          title="Open in-window viewer"
        >
          <Eye className="w-3.5 h-3.5" />
          View
        </button>
        <button
          type="button"
          onClick={onDownload}
          disabled={busy === `dl:${file.filename}`}
          className="glass-button-secondary text-xs py-1 px-2 inline-flex items-center gap-1"
        >
          {busy === `dl:${file.filename}` ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Download
        </button>
        {perms.canCreate && (
          <label className="glass-button-secondary text-xs py-1 px-2 inline-flex items-center gap-1 cursor-pointer">
            <Upload className="w-3.5 h-3.5" />
            Replace
            <input
              id={inputId}
              type="file"
              accept=".snbt,text/plain"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onReplace(f);
                e.target.value = '';
              }}
            />
          </label>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="glass-button-secondary text-xs py-1 px-2 text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
